import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LibraryService } from '@service/library.service';
import { Library } from 'icreate/common/models/library';
import { Shipyard } from 'icreate/common/models/shipyard';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { libraryNameMaxLen, libraryValidator } from 'icreate/common/validators/library/library-validator';
import { shipyardValidator } from 'icreate/common/validators/shipyard/shipyard-validator';

enum LibraryInputName {
    shipyard = 'shipyard',
    name = 'name',
}

@Component({
    selector: 'app-library-dialog',
    templateUrl: './library-dialog.component.html',
    styleUrls: ['./library-dialog.component.css'],
    standalone: false,
})
export class LibraryDialogComponent {
    readonly SHIPYARD_INPUT_NAME = LibraryInputName.shipyard;
    readonly NAME_INPUT_NAME = LibraryInputName.name;

    readonly NAME_MAX_LEN = libraryNameMaxLen;

    shipyardName: string = '';
    shipyardNames: string[] = [];
    libraryName: string = null;
    nameError: string = '';

    libraryList: [];
    selectedShipyard: Shipyard;

    isLoading: boolean = false;

    editMode: boolean = false;
    validationErrors: ValidationErrorMap = {};
    component: { shipyardList: { id: string; name: string; vessels: any[] }[] };
    hasError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            shipyardList: Shipyard[];
            libraryList: Library[];
            selectedLibrary: Library;
        },
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<LibraryDialogComponent>,
        private libraryService: LibraryService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.data.shipyardList && this.data.shipyardList.length > 0) {
            this.shipyardNames = this.shipyardNames.concat(this.data.shipyardList.map(shipyard => shipyard.name));
        }

        if (this.data?.selectedLibrary) {
            this.selectedShipyard = this.data.shipyardList.find(
                shipyard => shipyard.id === this.data.selectedLibrary.shipyardId
            );
            this.shipyardName = this.selectedShipyard.name;

            this.libraryName = this.data.selectedLibrary.name;
            this.editMode = true;
        }

        this.dialogHasErrors();
    }

    selectShipyard(event: string) {
        this.shipyardName = event;
        this.selectedShipyard = this.data.shipyardList.find(shipyard => shipyard.name === this.shipyardName);
    }

    onInputChange(event) {
        if (event.name === this.NAME_INPUT_NAME) {
            this.libraryName = event.value;
            this.dialogHasErrors();
        }
    }

    disabledSaveButton() {
        return this.hasError || !this.shipyardName;
    }

    dialogHasErrors() {
        const validationData = {
            name: this.libraryName,
        };

        const errors = getValidationErrors(libraryValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        let isRepeated = false;
        if (this.data.libraryList) {
            this.data.libraryList.forEach(library => {
                if (library.name === this.libraryName && this.data?.selectedLibrary?.id !== library.id) {
                    isRepeated = true;
                }
            });
        }

        if (isRepeated) {
            mappedErrors['name'] = {
                message: this.translateService.instant('editor.library.create.error.title.repeat'),
            };
        }

        this.hasError = Object.keys(mappedErrors).length > 0;
        this.validationErrors = this.hasError ? mappedErrors : null;
    }

    close() {
        this.dialogRef.close();
    }

    async save() {
        try {
            this.isLoading = true;
            if (this.editMode) {
                await this.libraryService.update({
                    ...this.data.selectedLibrary,
                    shipyardId: this.selectedShipyard.id,
                    name: this.libraryName,
                });
            } else {
                await this.libraryService.create({
                    shipyardId: this.selectedShipyard.id,
                    name: this.libraryName,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
            this.dialogRef.close();
        }
    }

    public get shipyardValidatorFunction() {
        return shipyardValidator;
    }

    public get libraryValidatorFunction() {
        return libraryValidator;
    }
}
